import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { HelpToggle } from '@/components/common/HelpToggle';
import { guidesCollapsed } from '@/lib/guideDisplay';

interface ExplainerItem {
  term: string;
  body: string;
}

/**
 * What the reader is looking at: every panel on the page, named and defined.
 *
 * The product rule is that no number is an orphan — each figure must be
 * traceable to its population and its denominator. `Answer`/`PopulationNote`
 * does that for a single figure at the point of display; this does the other
 * half, which is telling a reader what the figure IS before they interpret it.
 * A sanction percentile and a tax-credit percentile look identical on screen and
 * mean entirely different things.
 *
 * Definitions describe WHAT a figure is and HOW it is computed. They deliberately
 * carry no current values: all three agency corpora are rebuilt by ETL runs that
 * truncate and reload, so a count written into a locale file is true until the
 * next reload and misleading afterwards, with nothing to signal the change. Live
 * numbers come from the API and appear beside the figure they describe. The only
 * facts stated here are structural ones — that PAF credit is not a fine, that
 * propositions are model output — which a reload cannot alter.
 *
 * A native `<details>`, not a JS disclosure: it is keyboard accessible and
 * screen-reader announced without any of our code, and it collapses so a reader
 * who already knows the page is not made to scroll past the tutorial each visit.
 */
export function ExplainerCard({
  i18nKey,
  inline = false,
}: {
  i18nKey: string;
  /** Open and full width, whatever the page. The method page collects every explainer this way. */
  inline?: boolean;
}) {
  const { t } = useTranslation(['anatel', 'common']);

  const items = t(`${i18nKey}.items`, { returnObjects: true, defaultValue: [] });
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  if (!inline && guidesCollapsed()) {
    return (
      <HelpToggle title={t(`${i18nKey}.title`)}>
        <dl className="space-y-3 text-xs">
          {(items as ExplainerItem[]).map((item) => (
            <div key={item.term} className="space-y-0.5">
              <dt className="font-medium text-foreground">{item.term}</dt>
              <dd className="text-muted-foreground">{item.body}</dd>
            </div>
          ))}
        </dl>
      </HelpToggle>
    );
  }

  return (
    <details open={inline || undefined} className="group rounded-lg border bg-card">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-foreground">
        <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {t(`${i18nKey}.title`)}
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {t('common:explainer.toggle')}
        </span>
      </summary>
      <dl className="space-y-3 border-t px-3 py-3 text-xs">
        {(items as ExplainerItem[]).map((item) => (
          <div key={item.term} className="space-y-0.5">
            <dt className="font-medium text-foreground">{item.term}</dt>
            <dd className="text-muted-foreground">{item.body}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
